
import React, { useState, useEffect, useMemo } from 'react';
import { UserRole, User } from '../types';
import {
   LayoutDashboard, Users, Briefcase, Shield, Settings, LogOut,
   Building2, ChevronDown, ChevronRight, ChevronLeft, Menu, Zap, Bell, Search,
   User as UserIcon, X, CreditCard, ShoppingBag, ClipboardList, Database,
   Layout as LayoutIcon, Headphones, HelpCircle, ShieldAlert, Globe, FileText,
   Palette, Plus, LayoutTemplate, Eye, EyeOff, UserCircle2, Monitor, Home, ShieldCheck,
   GitPullRequest, FileEdit, Sparkles, FileSignature, Download, LayoutGrid, GripVertical,
   MessageCircle, Phone, CalendarDays, Kanban, FileSpreadsheet, BarChart2, Moon, Sun, Mail, CheckCircle2
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import RecruiterAgent from './RecruiterAgent';
import WerftoolsLogo from './WerftoolsLogo';
import TodoWidget from './TodoWidget';
import PWAInstallPrompt, { triggerPWAInstall } from './PWAInstallPrompt';
import GlobalSearch from './GlobalSearch';
import SupportSchedulerModal from './SupportSchedulerModal';
import { CLIENT_PORTAL_LOGO_URL, WERFTOOLS_LOGO_URL } from '../constants';

/** Merge saved order with defaults; unknown ids dropped; missing ids appended in default order. */
function mergeSidebarOrder(saved: string[] | undefined, defaultOrderedIds: string[]): string[] {
   const allowed = new Set(defaultOrderedIds);
   const out: string[] = [];
   if (saved?.length) {
      for (const id of saved) {
         if (allowed.has(id) && !out.includes(id)) out.push(id);
      }
   }
   for (const id of defaultOrderedIds) {
      if (!out.includes(id)) out.push(id);
   }
   return out;
}

interface LayoutProps {
   children: React.ReactNode;
   user: User;
   onLogout: () => void;
   activeTab: string;
   /** Engagement Center op tweede scherm: smalle URL met ?cockpit=1 — desktop-sidebar verborgen. */
   engagementCockpitLayout?: boolean;
   setActiveTab: (tab: string) => void;
   /** Verborgen apps + volgorde in `profiles.data` (`sidebar_hidden_ids`, `sidebar_order_ids`). */
   onSaveSidebarPrefs?: (prefs: { hiddenIds: string[]; orderIds: string[] }) => Promise<void>;
   onPerspectiveChange?: (role: UserRole) => void;
   onOpenChat?: () => void;
   taskbarItems?: Array<{ id: string, type: string, title: string }>;
   activeTaskbarId?: string | null;
   onTaskbarClick?: (item: any) => void;
   onRemoveTaskbarItem?: (id: string, e: any) => void;
   contentLoading?: boolean;
}

const Layout: React.FC<LayoutProps> = ({
   children,
   user,
   onLogout,
   activeTab,
   engagementCockpitLayout = false,
   setActiveTab,
   onSaveSidebarPrefs,
   onPerspectiveChange,
   onOpenChat,
   taskbarItems = [],
   activeTaskbarId,
   onTaskbarClick,
   onRemoveTaskbarItem,
   contentLoading: _contentLoading,
}) => {
   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
   const [isCollapsed, setIsCollapsed] = useState(false);
   const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
   const [isContactMenuOpen, setIsContactMenuOpen] = useState(false);
   const [isTodoMenuOpen, setIsTodoMenuOpen] = useState(false);
   const [showCreateMenu, setShowCreateMenu] = useState(false);
   const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
   const [froukjeAvatarErr, setFroukjeAvatarErr] = useState(false);
   const [menuPrefsOpen, setMenuPrefsOpen] = useState(false);
   const [menuPrefsDraftHidden, setMenuPrefsDraftHidden] = useState<string[]>([]);
   const [menuPrefsDraftOrder, setMenuPrefsDraftOrder] = useState<string[]>([]);
   const [menuPrefsSaving, setMenuPrefsSaving] = useState(false);

   useEffect(() => {
      setFroukjeAvatarErr(false);
   }, [user.branding?.aiRecruiter?.avatarUrl]);
   
   // Dark Mode Logic
   const [isDarkMode, setIsDarkMode] = useState(false);
   useEffect(() => {
       const hour = new Date().getHours();
       // Auto dark mode if after 8 PM or before 7 AM
       const isNight = hour >= 20 || hour < 7;
       if (isNight) {
           setIsDarkMode(true);
           document.documentElement.classList.add('dark');
       }
   }, []);

   const toggleDarkMode = () => {
       if (isDarkMode) {
           document.documentElement.classList.remove('dark');
           setIsDarkMode(false);
       } else {
           document.documentElement.classList.add('dark');
           setIsDarkMode(true);
       }
   };

   const isClientPortal = window.location.hostname.toLowerCase().includes('clientportal') || new URLSearchParams(window.location.search).get('clientportal') === 'true';

   const hideDesktopSidebarForCockpit =
      engagementCockpitLayout && activeTab === 'engagement_center' && !isClientPortal;

   const openCreateEntity = (entity: string, tab: string) => {
      if (isClientPortal && entity === 'vacancy') {
         alert('Upgrade vereist: Deze actie vereist een premium Werftools licentie. Neem contact op met uw recruiter om actieve bewerkingen uit te voeren.');
         setShowCreateMenu(false);
         return;
      }

      setActiveTab(tab);
      setShowCreateMenu(false);
      // Let React update the activeTab and render the child component, then dispatch the event
      setTimeout(() => {
         window.dispatchEvent(new CustomEvent(`open-new-${entity}`));
      }, 300);
   };

   const isWerftoolsStaff = user.role === UserRole.RECRUITER || user.role === UserRole.ADMIN;
   const isAdmin = user.role === UserRole.ADMIN;

   const navItemsEligible = useMemo(() => {
      const raw = [
         { id: 'dashboard', label: 'Overzicht', icon: <LayoutDashboard size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER, UserRole.CLIENT_RECRUITER] },
         { id: 'pipeline', label: 'Pipeline', icon: <Kanban size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER, UserRole.CLIENT_RECRUITER] },
         { id: 'shortlist', label: 'Shortlist', icon: <FileSpreadsheet size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER, UserRole.CLIENT_RECRUITER] },
         { id: 'workspace', label: 'Recruiter Hub', icon: <LayoutIcon size={16} />, roles: [UserRole.RECRUITER, UserRole.ADMIN, UserRole.CLIENT, UserRole.CLIENT_RECRUITER] },
         { id: 'vacancies', label: 'Vacatures', icon: <Briefcase size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER, UserRole.CLIENT_RECRUITER] },
         { id: 'vacancy_templates', label: 'Sjablonen', icon: <LayoutTemplate size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER, UserRole.CLIENT_RECRUITER] },
         { id: 'talentpool', label: 'Kandidaten', icon: <Users size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER, UserRole.CLIENT_RECRUITER] },
         { id: 'placements', label: 'Plaatsingen', icon: <CheckCircle2 size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER, UserRole.CLIENT_RECRUITER] },
         {
            id: 'reporting',
            label: 'Rapportage',
            icon: <BarChart2 size={16} />,
            roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER, UserRole.CLIENT_RECRUITER],
            subItems: [
               { id: 'reporting', label: 'Dashboard' },
               { id: 'reporting_kpi', label: 'KPI-doelen' },
               { id: 'reporting_ratios', label: "Ratio's" },
            ],
         },
         {
            id: 'documentbuilder',
            label: 'CV Builder',
            icon: <FileSignature size={16} />,
            roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER],
            subItems: [
               { id: 'docs', label: 'Documenten' },
               { id: 'cv_templates', label: 'Templates (.docx)' },
               { id: 'doc_shortcodes', label: 'Shortcodes & Tags' },
            ],
         },

         { id: 'engagement_center', label: 'Engagement', icon: <Sparkles size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER, UserRole.CLIENT_RECRUITER], beta: true },
         { id: 'automation', label: 'Automatisering', icon: <Zap size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER] },
         { id: 'clients', label: 'Toolklanten', icon: <Building2 size={16} />, roles: [UserRole.ADMIN] },
         { id: 'crm', label: 'Relaties', icon: <UserCircle2 size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER, UserRole.CLIENT_RECRUITER] },
         { id: 'careers_site', label: 'Site Editor', icon: <Globe size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.RECRUITER] },
         { id: 'products', label: 'Marketplace', icon: <ShoppingBag size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT, UserRole.CLIENT_RECRUITER] },
         { id: 'users', label: 'Gebruikers', icon: <ShieldAlert size={16} />, roles: [UserRole.ADMIN, UserRole.CLIENT] },
      ];
      return raw.filter(item => {
         if (isClientPortal && !['vacancies', 'talentpool', 'reporting', 'products', 'placements', 'pipeline', 'shortlist', 'engagement_center'].includes(item.id)) {
            return false;
         }

         const roleAllowed = item.roles.includes(user.role);
         const isStaff =
            user.role === UserRole.ADMIN || user.role === UserRole.RECRUITER;
         const permissionAllowed = isStaff
            ? !user.visibleTabs?.length || user.visibleTabs.includes(item.id)
            : Boolean(user.visibleTabs?.includes(item.id));

         return roleAllowed && permissionAllowed;
      });
   }, [isClientPortal, user.role, user.visibleTabs]);

   const sidebarHiddenSet = useMemo(() => new Set(user.sidebarHiddenIds ?? []), [user.sidebarHiddenIds]);

   const orderedEligible = useMemo(() => {
      const defaultIds = navItemsEligible.map(i => i.id);
      const order = mergeSidebarOrder(user.sidebarOrderIds, defaultIds);
      const byId = new Map(navItemsEligible.map(i => [i.id, i]));
      return order.map(id => byId.get(id)).filter(Boolean) as typeof navItemsEligible;
   }, [navItemsEligible, user.sidebarOrderIds]);

   const mainItems = useMemo(
      () =>
         orderedEligible.filter(
            item => item.id === 'dashboard' || !sidebarHiddenSet.has(item.id)
         ),
      [orderedEligible, sidebarHiddenSet]
   );

   const menuPersonalizableItems = useMemo(
      () => navItemsEligible.filter(item => item.id !== 'dashboard'),
      [navItemsEligible]
   );

   const personalizableById = useMemo(
      () => new Map(menuPersonalizableItems.map(i => [i.id, i])),
      [menuPersonalizableItems]
   );

   const openMenuPersonalization = () => {
      const defaultIds = menuPersonalizableItems.map(i => i.id);
      setMenuPrefsDraftOrder(mergeSidebarOrder(user.sidebarOrderIds, defaultIds));
      const eligibleIds = new Set(defaultIds);
      setMenuPrefsDraftHidden((user.sidebarHiddenIds ?? []).filter(id => eligibleIds.has(id)));
      setMenuPrefsOpen(true);
      setIsAccountMenuOpen(false);
   };

   const onAppsDragEnd = (result: DropResult) => {
      if (!result.destination) return;
      const next = Array.from(menuPrefsDraftOrder);
      const [removed] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, removed);
      setMenuPrefsDraftOrder(next);
   };

   const toggleMenuPrefHidden = (id: string, hide: boolean) => {
      setMenuPrefsDraftHidden(prev => {
         const next = new Set(prev);
         if (hide) next.add(id);
         else next.delete(id);
         return Array.from(next);
      });
   };

   const saveMenuPersonalization = async () => {
      if (!onSaveSidebarPrefs) return;
      const eligibleIds = new Set(menuPersonalizableItems.map(i => i.id));
      const hiddenIds = menuPrefsDraftHidden.filter(id => eligibleIds.has(id));
      const orderIds = menuPrefsDraftOrder.filter(id => eligibleIds.has(id));
      setMenuPrefsSaving(true);
      try {
         await onSaveSidebarPrefs({ hiddenIds, orderIds });
         setMenuPrefsOpen(false);
      } finally {
         setMenuPrefsSaving(false);
      }
   };

   const brandLogo = isClientPortal ? CLIENT_PORTAL_LOGO_URL : WERFTOOLS_LOGO_URL;
   const brandColor = user.branding?.primaryColor || '#e51b23'; // Default Werftools Red

   return (
      <div className="flex w-full h-[100dvh] bg-[#F8FAFC] font-sans text-slate-900 overflow-hidden">
         {/* Global CSS Override for Brand Colors */}
         <style>{`
            :root {
              --color-brand-red: ${brandColor};
            }
            /* Specific overrides for hardcoded Tailwind classes (bg-[#e51b23], text-[#e51b23], border-[#e51b23]) */
            .bg-\\[\\#e51b23\\] { background-color: var(--color-brand-red) !important; }
            .text-\\[\\#e51b23\\] { color: var(--color-brand-red) !important; }
            .border-\\[\\#e51b23\\] { border-color: var(--color-brand-red) !important; }
            .hover\\:bg-\\[\\#B71C1C\\]:hover { filter: brightness(0.85); background-color: var(--color-brand-red) !important; }
            .ring-\\[\\#e51b23\\] { --tw-ring-color: var(--color-brand-red) !important; }
            .focus\\:ring-\\[\\#e51b23\\]:focus { --tw-ring-color: var(--color-brand-red) !important; }

            ${isClientPortal ? `
            @media (min-width: 1024px) {
               html {
                  font-size: 75% !important; /* 12px instead of 16px, scales all Tailwind rem units by 0.75 exactly like native browser zoom */
               }
            }
            ` : ''}
         `}</style>

         {/* DESKTOP SIDEBAR (Compact Floating Glass Style) */}
         <aside className={`
        ${hideDesktopSidebarForCockpit ? 'hidden' : 'hidden lg:flex'} flex-col m-1 rounded-xl shadow-[0_10px_30px_-10px_rgba(0,0,0,0.05)] transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] z-20
        ${isCollapsed ? 'w-16' : 'w-48 xl:w-52 2xl:w-60'}
        ${isAdmin ? 'bg-[#e51b23] text-white border-transparent' : 'bg-white border border-gray-100 text-slate-900'}
      `}>
            {/* Header */}
            <div className="h-20 flex items-center justify-center relative">
               <div className={`flex items-center gap-2 transition-all ${isCollapsed ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}>
                  {brandLogo ? (
                     <div className="flex items-center max-w-full">
                        <img src={brandLogo} alt="Logo" className="h-8 xl:h-9 w-auto max-w-[100px] xl:max-w-[120px] object-contain" />
                     </div>
                  ) : (
                     <div className="flex items-center max-w-full">
                        <img src={isClientPortal ? CLIENT_PORTAL_LOGO_URL : WERFTOOLS_LOGO_URL} alt="Logo" className="h-8 xl:h-9 w-auto max-w-[100px] xl:max-w-[120px] object-contain" />
                     </div>
                  )}
                  <div className="flex flex-col">
                     {brandLogo && <span className={`text-sm font-black tracking-tight truncate max-w-[120px] ${isAdmin ? 'text-white' : ''}`}>{user.clientName || 'Werftools'}</span>}
                     
                     
                  </div>
               </div>

               {isCollapsed && (
                  <div className="absolute inset-0 flex items-center justify-center">
                     {brandLogo ? (
                        <div className="w-9 h-9 flex items-center justify-center bg-slate-50 rounded-xl overflow-hidden border border-slate-100 p-1">
                           <img src={brandLogo} alt="Logo" className="w-full h-full object-contain" />
                        </div>
                     ) : (
                        <div className="w-9 h-9 flex items-center justify-center p-1">
                           <img src={isClientPortal ? CLIENT_PORTAL_LOGO_URL : WERFTOOLS_LOGO_URL} alt="Logo" className="w-full h-full object-contain" />
                        </div>
                     )}
                  </div>
               )}

               <button
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  className={`absolute -right-3 top-8 w-6 h-6 rounded-full flex items-center justify-center shadow-md transition-colors z-50 hover:scale-110 ${isAdmin ? 'bg-red-800 border-none text-white hover:bg-red-900' : 'bg-white border border-gray-100 text-slate-400 hover:text-slate-900'}`}
               >
                  {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
               </button>
            </div>



            <nav className="flex-1 px-3 space-y-1 overflow-y-auto no-scrollbar py-2">
               <p className="text-red-600 font-bold text-center">DEVELOPMENT ENVIRONMENT</p>
               {/* + Nieuw button in Sidebar */}
               {!isCollapsed && (
                  <div className="mb-4 mt-2 px-2 relative">
                     <button
                        onClick={() => setShowCreateMenu(!showCreateMenu)}
                        style={isAdmin ? {} : { backgroundColor: '#e51b23' }}
                        className={`w-full h-9 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-[0.97] shadow-sm hover:shadow-md ${isAdmin ? 'bg-slate-900 text-white hover:bg-slate-800' : 'text-white hover:brightness-110'}`}
                     >
                        <Plus size={16} className={`shrink-0 transition-transform duration-200 ${showCreateMenu ? 'rotate-45' : ''}`} />
                        <span className="text-xs font-bold tracking-wide">{showCreateMenu ? 'Sluiten' : 'Nieuw'}</span>
                     </button>
                     {showCreateMenu && (
                        <div className="absolute top-[calc(100%+8px)] left-2 right-2 bg-white border border-slate-100 rounded-xl shadow-xl z-50 py-1.5 animate-slideDown origin-top">
                           <button onClick={() => openCreateEntity('vacancy', 'vacancies')} className="w-full text-left px-3 py-2 text-[12px] font-medium text-slate-700 hover:bg-slate-50 hover:text-[#e51b23] transition-colors flex items-center gap-2.5">
                              <Briefcase size={14} className="text-slate-400" /> Vacature
                           </button>
                           {user.role === UserRole.ADMIN && (
                              <button onClick={() => openCreateEntity('client', 'clients')} className="w-full text-left px-3 py-2 text-[12px] font-medium text-slate-700 hover:bg-slate-50 hover:text-[#e51b23] transition-colors flex items-center gap-2.5">
                                 <Building2 size={14} className="text-slate-400" /> SaaS Klant
                              </button>
                           )}
                           <button onClick={() => openCreateEntity('crm', 'crm')} className="w-full text-left px-3 py-2 text-[12px] font-medium text-slate-700 hover:bg-slate-50 hover:text-[#e51b23] transition-colors flex items-center gap-2.5">
                              <UserCircle2 size={14} className="text-slate-400" /> Relatie (CRM)
                           </button>
                           <button onClick={() => openCreateEntity('candidate', 'talentpool')} className="w-full text-left px-3 py-2 text-[12px] font-medium text-slate-700 hover:bg-slate-50 hover:text-[#e51b23] transition-colors flex items-center gap-2.5">
                              <Users size={14} className="text-slate-400" /> Kandidaat
                           </button>
                           <div className="h-px bg-slate-100 mx-3 my-1" />
                           <button onClick={() => openCreateEntity('todo', 'dashboard')} className="w-full text-left px-3 py-2 text-[12px] font-medium text-slate-700 hover:bg-slate-50 hover:text-[#e51b23] transition-colors flex items-center gap-2.5">
                              <ClipboardList size={14} className="text-slate-400" /> Taak / Notitie
                           </button>
                           {(user.role === UserRole.ADMIN || user.role === UserRole.CLIENT) && (
                              <button onClick={() => openCreateEntity('user', 'users')} className="w-full text-left px-3 py-2 text-[12px] font-medium text-slate-700 hover:bg-slate-50 hover:text-[#e51b23] transition-colors flex items-center gap-2.5">
                                 <ShieldAlert size={14} className="text-slate-400" /> Gebruiker
                              </button>
                           )}
                           <button onClick={() => openCreateEntity('automation', 'automation')} className="w-full text-left px-3 py-2 text-[12px] font-medium text-slate-700 hover:bg-slate-50 hover:text-[#e51b23] transition-colors flex items-center gap-2.5">
                              <Zap size={14} className="text-slate-400" /> Automatisering
                           </button>
                        </div>
                     )}
                  </div>
               )}

               {mainItems.map(item => {
                  const isBeta = (item as { beta?: boolean }).beta;
                  const isActive =
                     activeTab === item.id ||
                     (item.subItems && item.subItems.some((sub: { id: string }) => activeTab === sub.id));

                  const baseClass = isAdmin
                     ? isActive
                        ? 'bg-white text-[#e51b23] font-black'
                        : 'text-white/75 hover:bg-red-800 hover:text-white'
                     : isActive
                       ? 'bg-slate-50 text-slate-900 font-bold border border-slate-100'
                       : 'text-slate-500 hover:bg-slate-50/50 hover:text-slate-800';

                  return (
                     <React.Fragment key={item.id}>
                        <button
                           type="button"
                           title={isBeta ? `${item.label} (BETA)` : item.label}
                           onClick={() => setActiveTab(item.id)}
                           className={`w-full flex items-center rounded-lg transition-all duration-200 group ${
                              isBeta ? 'min-h-10 h-auto py-1.5' : 'h-8 xl:h-9 2xl:h-10'
                           } ${baseClass} ${isCollapsed ? 'justify-center px-0' : 'px-2 gap-2 2xl:px-3 2xl:gap-3'}`}
                        >
                           <div
                              style={{
                                 color: isActive ? (isAdmin ? '#e51b23' : brandColor) : undefined,
                              }}
                              className={`shrink-0 transition-transform duration-200 ${
                                 isActive ? 'scale-110' : 'group-hover:scale-105'
                              }`}
                           >
                              {item.icon}
                           </div>

                           {!isCollapsed && isBeta ? (
                              <div className="flex flex-col items-start justify-center flex-1 min-w-0 leading-tight gap-0.5">
                                 <span className="text-xs font-bold tracking-tight">{item.label}</span>
                                 <span className="text-[8px] font-black text-[#e51b23] tracking-[0.2em] uppercase">
                                    BETA
                                 </span>
                              </div>
                           ) : (
                              !isCollapsed && (
                                 <span className="text-xs font-bold tracking-tight flex-1 text-left">
                                    {item.label}
                                 </span>
                              )
                           )}

                           {!isCollapsed && activeTab === item.id && (
                              <div
                                 style={{ backgroundColor: brandColor }}
                                 className="ml-auto shrink-0 w-1.5 h-1.5 rounded-full self-center"
                              />
                           )}
                        </button>

                        {'subItems' in item &&
                           item.subItems &&
                           !isCollapsed &&
                           (activeTab === item.id ||
                              item.subItems.some((sub: { id: string }) => activeTab === sub.id)) && (
                              <div className="pl-9 pr-2 py-1 space-y-1 mt-1 bg-slate-50/30 rounded-lg border-l-2 border-slate-100 ml-3">
                                 {item.subItems.map((sub: { id: string; label: string }, subIdx: number) => (
                                    <button
                                       key={`${item.id}-sub-${subIdx}-${sub.id}`}
                                       type="button"
                                       onClick={() => setActiveTab(sub.id)}
                                       className={`w-full flex items-center h-7 rounded-md px-3 text-[10px] font-bold tracking-wide transition-all ${
                                          activeTab === sub.id
                                             ? 'bg-white text-slate-900 border border-slate-200 shadow-sm'
                                             : 'text-slate-400 hover:text-slate-700 hover:bg-white'
                                       }`}
                                    >
                                       {sub.label}
                                       {activeTab === sub.id && (
                                          <div
                                             style={{ backgroundColor: brandColor }}
                                             className="ml-auto w-1 h-1 rounded-full"
                                          />
                                       )}
                                    </button>
                                 ))}
                              </div>
                           )}
                     </React.Fragment>
                  );
               })}
            </nav>

            {/* Version Tracker */}
            {!isCollapsed && (
               <div className="px-5 mt-auto pb-2 flex justify-center opacity-50">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest bg-slate-100 px-2 py-0.5 rounded-full">
                     V2.03 (Werftools)
                  </span>
               </div>
            )}

            {/* Footer Profile */}
            <div className="p-2 2xl:p-3 flex flex-col gap-2">
               {onSaveSidebarPrefs && !isClientPortal && menuPersonalizableItems.length > 0 && (
                  <button
                     type="button"
                     onClick={openMenuPersonalization}
                     title="Apps & menu"
                     className={`w-full flex items-center p-2 rounded-xl border border-slate-200/80 bg-white/90 shadow-sm hover:shadow-md hover:border-slate-300 transition-all ${
                        isAdmin ? 'border-white/20 bg-white/10 hover:bg-white/15' : ''
                     } ${isCollapsed ? 'justify-center' : 'gap-3'}`}
                  >
                     <div
                        className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${
                           isAdmin
                              ? 'bg-white/15 text-white border border-white/20'
                              : 'bg-slate-900 text-white'
                        }`}
                     >
                        <LayoutGrid size={16} />
                     </div>
                     {!isCollapsed && (
                        <div className="flex flex-col items-start min-w-0 text-left">
                           <span
                              className={`text-xs font-bold truncate ${isAdmin ? 'text-white' : 'text-slate-900'}`}
                           >
                              Apps & menu
                           </span>
                           <span
                              className={`text-[9px] font-black uppercase tracking-widest truncate ${
                                 isAdmin ? 'text-white/70' : 'text-slate-400'
                              }`}
                           >
                              Volgorde & zichtbaar
                           </span>
                        </div>
                     )}
                  </button>
               )}

               {onOpenChat && !isClientPortal && (
                  <button
                     onClick={onOpenChat}
                     className={`w-full flex items-center p-2 rounded-xl border border-purple-100 bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 transition-all ${isCollapsed ? 'justify-center' : 'gap-3'}`}
                     title={user.branding?.aiRecruiter?.name ? `Vraag aan ${user.branding.aiRecruiter.name}` : "Vraag aan Froukje"}
                  >
                     <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#e51b23] to-pink-500 flex items-center justify-center text-white shrink-0 shadow-sm overflow-hidden">
                        {user.branding?.aiRecruiter?.avatarUrl && !froukjeAvatarErr ? (
                           <img
                              src={user.branding.aiRecruiter.avatarUrl}
                              alt="AI Avatar"
                              className="w-full h-full object-cover"
                              onError={() => setFroukjeAvatarErr(true)}
                           />
                        ) : (
                           <Sparkles size={14} />
                        )}
                     </div>
                     {!isCollapsed && (
                        <div className="flex flex-col items-start min-w-0">
                           <span className="text-xs font-bold text-slate-900 truncate">Vraag aan {user.branding?.aiRecruiter?.name || 'Froukje'}</span>
                           <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest truncate">Data Analist</span>
                        </div>
                     )}
                  </button>
               )}

               <div
                  onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                  className={`flex items-center p-1.5 rounded-xl bg-slate-50 border border-slate-100 cursor-pointer hover:bg-slate-100 transition-all ${isCollapsed ? 'justify-center' : 'gap-2'}`}
               >
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-xs font-black border border-slate-100 text-slate-900 shadow-sm shrink-0">
                     {user.name.charAt(0)}
                  </div>
                  {!isCollapsed && (
                     <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-bold text-slate-900 truncate">{user.name}</p>
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider truncate">{user.role}</p>
                     </div>
                  )}
               </div>

               {isAccountMenuOpen && !isCollapsed && (
                  <div className="mt-2 space-y-1 animate-slideUp bg-white border border-slate-100 rounded-xl p-1 shadow-xl">
                     <button onClick={() => { setActiveTab('settings'); setIsAccountMenuOpen(false); }} className="w-full flex items-center gap-3 p-2.5 rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors text-[10px] font-bold uppercase tracking-wide">
                        <Settings size={14} /> Instellingen
                     </button>
                     <button onClick={() => { triggerPWAInstall(); setIsAccountMenuOpen(false); }} className="w-full lg:hidden flex items-center gap-3 p-2.5 rounded-lg text-slate-500 hover:bg-slate-50 hover:text-[#e51b23] transition-colors text-[10px] font-bold uppercase tracking-wide">
                        <Download size={14} /> Installeer App
                     </button>
                     <button onClick={onLogout} className="w-full flex items-center gap-3 p-2.5 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors text-[10px] font-bold uppercase tracking-wide">
                        <LogOut size={14} /> Uitloggen
                     </button>
                  </div>
               )}
            </div>
         </aside>

         {/* MOBILE HEADER */}
         <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-md border-b border-gray-100 flex justify-between items-center px-4 z-50">
            <div className="flex items-center gap-3">
               <button onClick={() => setIsAccountMenuOpen(true)} className="p-2 -ml-2 text-slate-600 hover:text-slate-900 transition-colors">
                  <Menu size={24} />
               </button>
               <div className="flex items-center">
                  <img src={brandLogo} alt="Logo" className="h-7 w-auto max-w-[130px] object-contain" />
               </div>
            </div>
            
            <div className="flex items-center gap-3">
               <GlobalSearch user={user} isMobile={true} />
               {/* WERFTOOLS ADMIN BADGE (MOBILE) */}
               {isAdmin && (
                  <div className="flex items-center gap-1.5 px-2 py-1.5 bg-slate-900 border border-slate-700 text-emerald-400 rounded-md shadow-sm">
                     <ShieldCheck size={12} />
                     <span className="text-[9px] font-black uppercase tracking-widest hidden sm:inline-block">Admin</span>
                  </div>
               )}
               <button
                  onClick={() => setIsAccountMenuOpen(true)}
                  className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-xs shadow-sm shadow-black/10 overflow-hidden ring-2 ring-transparent transition-all active:scale-95 hover:ring-slate-200"
                  style={{ backgroundColor: brandColor }}
               >
                  {((user as any).avatar_url || (user as any).avatarUrl) ? (
                     <img src={(user as any).avatar_url || (user as any).avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                     user.name.charAt(0)
                  )}
               </button>
            </div>
         </header>

         {/* MOBILE BOTTOM NAV */}
         <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 pb-safe pt-3 pb-6 px-2 rounded-t-3xl shadow-[0_-10px_40px_rgba(0,0,0,0.08)] z-50 flex justify-around items-end min-h-[6rem]">
            {isClientPortal ? (
               <>
                  <button onClick={() => setActiveTab('dashboard')} className={`flex flex-col items-center gap-1 w-14 transition-all pb-1 mt-1 ${activeTab === 'dashboard' ? 'text-slate-900 scale-105' : 'text-slate-400'}`}>
                     <LayoutDashboard size={activeTab === 'dashboard' ? 26 : 24} className={activeTab === 'dashboard' ? 'text-[#e51b23]' : ''} />
                     <span className={`text-[9px] font-bold tracking-tight ${activeTab === 'dashboard' ? 'text-[#e51b23]' : ''}`}>Overzicht</span>
                  </button>

                  <button onClick={() => setActiveTab('vacancies')} className={`flex flex-col items-center gap-1 w-14 transition-all pb-1 mt-1 ${activeTab === 'vacancies' ? 'text-slate-900 scale-105' : 'text-slate-400'}`}>
                     <Briefcase size={activeTab === 'vacancies' ? 26 : 24} className={activeTab === 'vacancies' ? 'text-[#e51b23]' : ''} />
                     <span className={`text-[9px] font-bold tracking-tight ${activeTab === 'vacancies' ? 'text-[#e51b23]' : ''}`}>Vacatures</span>
                  </button>

                  <button onClick={() => setIsContactMenuOpen(true)} className="flex flex-col items-center justify-center -mt-8 relative group z-10 w-16">
                     <div className="w-16 h-16 bg-white rounded-full p-2.5 shadow-[0_0_25px_rgba(0,0,0,0.15)] border border-gray-50 flex items-center justify-center transform active:scale-95 transition-all">
                        <img src="https://xquintpfpzdsxupnhypj.supabase.co/storage/v1/object/public/public/website/Favicon%20werfplaats%20(2).png" alt="Werfplaats Contact" className="w-full h-full object-contain" />
                     </div>
                     <span className="text-[10px] font-bold text-slate-500 mt-1 tracking-tight">Contact</span>
                  </button>

                  <button onClick={() => setActiveTab('talentpool')} className={`flex flex-col items-center gap-1 w-14 transition-all pb-1 mt-1 ${activeTab === 'talentpool' ? 'text-slate-900 scale-105' : 'text-slate-400'}`}>
                     <Users size={activeTab === 'talentpool' ? 26 : 24} className={activeTab === 'talentpool' ? 'text-[#e51b23]' : ''} />
                     <span className={`text-[9px] font-bold tracking-tight ${activeTab === 'talentpool' ? 'text-[#e51b23]' : ''}`}>Talentpool</span>
                  </button>

                  <button onClick={() => setIsTodoMenuOpen(true)} className={`flex flex-col items-center gap-1 w-14 transition-all pb-1 mt-1 ${isTodoMenuOpen ? 'text-indigo-600 scale-105' : 'text-slate-400'}`}>
                     <ClipboardList size={isTodoMenuOpen ? 26 : 24} className={isTodoMenuOpen ? 'text-indigo-600' : ''} />
                     <span className={`text-[9px] font-bold tracking-tight ${isTodoMenuOpen ? 'text-indigo-600' : ''}`}>To do</span>
                  </button>
               </>
            ) : (
               mainItems.slice(0, 5).map(item => (
                  <button
                     key={item.id}
                     onClick={() => setActiveTab(item.id)}
                     className={`flex flex-col items-center gap-0.5 w-16 transition-all mt-1 ${activeTab === item.id ? 'text-[#e51b23] scale-105' : 'text-slate-400'}`}
                  >
                     {React.cloneElement(item.icon as any, { size: activeTab === item.id ? 24 : 22 })}
                     <span className="text-[9px] font-bold truncate w-full text-center px-1 tracking-tight leading-tight">{item.label.split(' ')[0]}</span>
                     {(item as { beta?: boolean }).beta && (
                        <span className="text-[7px] font-black text-[#e51b23] tracking-wide">BETA</span>
                     )}
                  </button>
               ))
            )}
         </nav>

         {/* MOBILE ACCOUNT MENU OVERLAY */}
         {isAccountMenuOpen && (
            <div className="lg:hidden fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex justify-end">
               <div className="w-64 bg-white h-full shadow-2xl animate-slideLeft flex flex-col">
                  <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-slate-50">
                     <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-xs font-black border border-slate-200 text-slate-900 shadow-sm shrink-0">
                           {user.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                           <p className="text-[11px] font-bold text-slate-900 truncate">{user.name}</p>
                           <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider truncate">{user.role}</p>
                        </div>
                     </div>
                     <button onClick={() => setIsAccountMenuOpen(false)} className="text-slate-400 hover:text-[#e51b23] bg-slate-100 p-1 rounded-full">
                        <X size={16} />
                     </button>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                     {onSaveSidebarPrefs && !isClientPortal && menuPersonalizableItems.length > 0 && (
                        <button
                           type="button"
                           onClick={openMenuPersonalization}
                           className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100 transition-colors text-[10px] font-bold uppercase tracking-wide"
                        >
                           <LayoutGrid size={16} className="text-slate-600" /> Apps & menu
                        </button>
                     )}
                     <button onClick={() => { setActiveTab('settings'); setIsAccountMenuOpen(false); }} className="w-full flex items-center gap-3 p-3 rounded-xl text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-[10px] font-bold uppercase tracking-wide">
                        <Settings size={16} className="text-slate-400" /> Instellingen
                     </button>
                     <button onClick={() => { triggerPWAInstall(); setIsAccountMenuOpen(false); }} className="w-full flex items-center gap-3 p-3 rounded-xl text-slate-600 hover:bg-red-50 hover:text-[#e51b23] border border-red-50 transition-colors text-[10px] font-bold uppercase tracking-wide">
                        <Download size={16} className="text-[#e51b23]" /> Installeer App
                     </button>
                     <div className="h-px bg-slate-100 my-2"></div>
                     <button onClick={onLogout} className="w-full flex items-center gap-3 p-3 rounded-xl text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors text-[10px] font-bold uppercase tracking-wide">
                        <LogOut size={16} className="text-red-400" /> Uitloggen
                     </button>
                  </div>
               </div>
            </div>
         )}

         {/* MOBILE CONTACT MENU OVERLAY */}
         {isContactMenuOpen && (
            <div className="lg:hidden fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex justify-center items-end pb-24 px-4">
               <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl animate-slideUp overflow-hidden">
                  <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-slate-50">
                     <h3 className="font-black text-slate-900 tracking-tight">Contact Werfplaats</h3>
                     <button onClick={() => setIsContactMenuOpen(false)} className="text-slate-400 hover:text-[#e51b23] bg-slate-100 p-1 rounded-full">
                        <X size={16} />
                     </button>
                  </div>
                  <div className="p-4 space-y-3">
                      <button onClick={() => { setIsContactMenuOpen(false); setIsSupportModalOpen(true); }} className="w-full p-4 flex items-center gap-3 rounded-2xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all group">
                         <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                            <CalendarDays size={20} />
                         </div>
                         <div className="text-left flex-1">
                            <p className="text-xs font-black text-slate-900 uppercase tracking-widest group-hover:text-indigo-700">Maak Afspraak</p>
                            <p className="text-[10px] font-bold text-slate-500">Plan een moment in met Thom</p>
                         </div>
                      </button>
                      <a href="mailto:t.devries@werfplaats.nl?subject=Vraag via Werfplaats" className="w-full p-4 flex items-center gap-3 rounded-2xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all group">
                         <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                            <Mail size={20} />
                         </div>
                         <div className="text-left flex-1">
                            <p className="text-xs font-black text-slate-900 uppercase tracking-widest group-hover:text-blue-700">E-mail</p>
                            <p className="text-[10px] font-bold text-slate-500">Stuur een mail naar Thom</p>
                         </div>
                      </a>
                      <a href="https://wa.me/31624800166?text=Hi%20Thom,%20ik%20heb%20een%20vraag%20over%20Werfplaats." target="_blank" rel="noopener noreferrer" className="w-full p-4 flex items-center gap-3 rounded-2xl border border-slate-200 hover:border-green-300 hover:bg-green-50 transition-all group">
                         <div className="w-10 h-10 rounded-xl bg-green-100 text-green-600 flex items-center justify-center shrink-0">
                            <MessageCircle size={20} />
                         </div>
                         <div className="text-left flex-1">
                            <p className="text-xs font-black text-slate-900 uppercase tracking-widest group-hover:text-green-700">WhatsApp</p>
                            <p className="text-[10px] font-bold text-slate-500">Direct contact via WhatsApp</p>
                         </div>
                      </a>
                   </div>
               </div>
            </div>
         )}

          {/* MAIN CONTENT AREA */}
          <main className="flex-1 flex flex-col min-w-0 relative h-full overflow-hidden lg:pr-1 pt-16 lg:pb-1 lg:pt-1">
             <div className={`hidden lg:flex items-center justify-between w-full min-h-[64px] py-2 px-6 shrink-0 relative z-50 ${hideDesktopSidebarForCockpit ? 'pl-4' : 'pl-8'}`}>
                <div className="flex-1 max-w-full pr-8 flex items-center flex-wrap gap-4">
                   <GlobalSearch user={user} className="w-full max-w-xs shrink-0" />
                   
                   {/* TASKBAR SHORTCUTS */}
                   {taskbarItems && taskbarItems.length > 0 && (
                      <div className="flex items-center flex-wrap gap-1 flex-1 pb-1 pt-1 border-l border-slate-200 pl-3">
                         {taskbarItems.map(item => (
                             <div 
                               key={item.id} 
                               className={`flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer text-[10px] font-bold transition-all duration-200 min-w-[70px] max-w-[130px] shrink-0 border ${activeTaskbarId === item.id ? 'bg-slate-900 text-white border-slate-700 shadow-sm' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50 hover:text-slate-800'}`}
                               onClick={() => onTaskbarClick && onTaskbarClick(item)}
                             >
                                {item.type === 'vacancy' ? <Briefcase size={10} className={activeTaskbarId === item.id ? 'text-blue-400' : ''}/> : item.type === 'candidate' ? <UserIcon size={10} className={activeTaskbarId === item.id ? 'text-emerald-400' : ''}/> : <Database size={10} className={activeTaskbarId === item.id ? 'text-purple-400' : ''}/>}
                                <span className="truncate flex-1">{item.title}</span>
                                <button onClick={(e) => onRemoveTaskbarItem && onRemoveTaskbarItem(item.id, e)} className={`ml-1 p-0.5 rounded-full hover:bg-slate-700 transition-colors ${activeTaskbarId === item.id ? 'text-slate-300 hover:text-white' : 'text-slate-400 hover:bg-slate-200 hover:text-red-500'}`}>
                                    <X size={10}/>
                                </button>
                             </div>
                          ))}
                      </div>
                   )}
                </div>
                
                <div className="flex items-center gap-3 relative shrink-0">
                   <button 
                      onClick={toggleDarkMode}
                      className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors shadow-sm"
                      title={isDarkMode ? "Lichte Modus" : "Donkere Modus"}
                   >
                      {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
                   </button>
                   
                   {/* SUPPORT COMPACT BUTTON */}
                   <button 
                       onClick={() => setIsSupportModalOpen(true)}
                       className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-colors shadow-sm"
                       title="Support / Afspraak met Thom"
                   >
                       <HelpCircle size={16} />
                   </button>

                  {/* WERFTOOLS ADMIN BADGE (DESKTOP) */}
                  {isAdmin && (
                     <div className="flex items-center gap-2 px-4 py-2 bg-slate-900/90 backdrop-blur-md border border-slate-700 text-emerald-400 rounded-lg shadow-[0_0_15px_rgba(52,211,153,0.15)] animate-pulse shrink-0">
                        <ShieldCheck size={16} />
                        <span className="text-[10px] font-black uppercase tracking-widest text-emerald-300">Werftools Systeembeheerder</span>
                     </div>
                  )}
               </div>
            </div>
            
            <div className="flex-1 bg-white overflow-y-auto no-scrollbar relative w-full h-full lg:rounded-xl lg:border lg:border-slate-200 lg:shadow-sm">
               {children}
            </div>
         </main>

         {menuPrefsOpen && onSaveSidebarPrefs && (
            <div
               role="presentation"
               className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md"
               onClick={() => !menuPrefsSaving && setMenuPrefsOpen(false)}
            >
               <div
                  role="dialog"
                  aria-labelledby="menu-prefs-title"
                  className="flex min-h-0 max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_24px_80px_-12px_rgba(0,0,0,0.35)]"
                  onClick={e => e.stopPropagation()}
               >
                  <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-[#1a0a0c] px-5 py-5">
                     <LayoutGrid
                        className="pointer-events-none absolute -right-6 -top-6 h-36 w-36 text-white/10"
                        strokeWidth={1}
                     />
                     <div className="relative flex justify-between gap-3">
                        <div className="min-w-0">
                           <p className="text-[10px] font-black uppercase tracking-[0.22em] text-white/45">
                              Werfplaats
                           </p>
                           <h2 id="menu-prefs-title" className="mt-1 text-xl font-black tracking-tight text-white">
                              Mijn apps
                           </h2>
                           <p className="mt-2 max-w-sm text-[13px] leading-snug text-white/65">
                              Sleep om te rangschikken. Zet een app uit om hem uit het linkermenu te halen. Opgeslagen op je profiel.
                           </p>
                        </div>
                        <button
                           type="button"
                           disabled={menuPrefsSaving}
                           onClick={() => setMenuPrefsOpen(false)}
                           className="h-9 w-9 shrink-0 rounded-xl bg-white/10 text-white hover:bg-white/20 disabled:opacity-50 flex items-center justify-center border border-white/10"
                        >
                           <X size={18} />
                        </button>
                     </div>
                  </div>

                  <DragDropContext onDragEnd={onAppsDragEnd}>
                     <div className="flex min-h-0 flex-1 flex-col bg-slate-50/90 px-1 pb-1">
                        <Droppable droppableId="sidebar-apps-order">
                           {(dropProvided, dropSnapshot) => (
                              <div
                                 ref={dropProvided.innerRef}
                                 {...dropProvided.droppableProps}
                                 className={`mx-3 mt-3 mb-1 max-h-[min(52vh,520px)] space-y-2 overflow-y-auto rounded-xl border border-slate-200/80 bg-white p-3 shadow-inner ${
                                    dropSnapshot.isDraggingOver ? 'ring-2 ring-[#e51b23]/25' : ''
                                 }`}
                              >
                                 {menuPrefsDraftOrder.map((id, index) => {
                                    const item = personalizableById.get(id);
                                    if (!item) return null;
                                    const hidden = menuPrefsDraftHidden.includes(id);
                                    return (
                                       <Draggable
                                          key={id}
                                          draggableId={id}
                                          index={index}
                                          isDragDisabled={menuPrefsSaving}
                                       >
                                          {(dragProvided, dragSnapshot) => (
                                             <div
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                className={`rounded-2xl border bg-white transition-[box-shadow,transform] ${
                                                   dragSnapshot.isDragging
                                                      ? 'border-[#e51b23]/40 shadow-xl ring-2 ring-[#e51b23]/20'
                                                      : hidden
                                                        ? 'border-slate-100 opacity-60'
                                                        : 'border-slate-100 shadow-sm'
                                                }`}
                                             >
                                                <div className="flex items-center gap-2 p-2">
                                                   <button
                                                      type="button"
                                                      title="Verslepen"
                                                      disabled={menuPrefsSaving}
                                                      {...dragProvided.dragHandleProps}
                                                      className="rounded-xl p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-40"
                                                   >
                                                      <GripVertical size={18} />
                                                   </button>
                                                   <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 text-slate-700 shadow-inner ring-1 ring-slate-100">
                                                      {React.cloneElement(
                                                         item.icon as React.ReactElement<{ size?: number }>,
                                                         { size: 22 }
                                                      )}
                                                   </div>
                                                   <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-900">
                                                      {item.label}
                                                      {(item as { beta?: boolean }).beta && (
                                                         <span className="ml-2 align-middle text-[9px] font-black uppercase tracking-widest text-[#e51b23]">
                                                            beta
                                                         </span>
                                                      )}
                                                   </span>
                                                   <label className="flex shrink-0 cursor-pointer items-center gap-2 rounded-xl px-2 py-1.5 hover:bg-slate-50">
                                                      <span className="hidden text-[10px] font-bold uppercase tracking-wide text-slate-400 sm:inline">
                                                         {hidden ? 'Uit' : 'Aan'}
                                                      </span>
                                                      <input
                                                         type="checkbox"
                                                         checked={!hidden}
                                                         disabled={menuPrefsSaving}
                                                         onChange={e =>
                                                            toggleMenuPrefHidden(item.id, !e.target.checked)
                                                         }
                                                         className="h-4 w-4 rounded border-slate-300 text-[#e51b23] focus:ring-[#e51b23]"
                                                      />
                                                   </label>
                                                </div>
                                             </div>
                                          )}
                                       </Draggable>
                                    );
                                 })}
                                 {dropProvided.placeholder}
                              </div>
                           )}
                        </Droppable>
                     </div>
                  </DragDropContext>

                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-white px-4 py-4">
                     <button
                        type="button"
                        disabled={menuPrefsSaving}
                        onClick={() => setMenuPrefsDraftHidden([])}
                        className="text-[10px] font-bold uppercase tracking-wide text-slate-500 hover:text-slate-800 disabled:opacity-50 px-2 py-2"
                     >
                        Alles tonen
                     </button>
                     <button
                        type="button"
                        onClick={saveMenuPersonalization}
                        disabled={menuPrefsSaving}
                        className="rounded-xl bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-lg hover:from-slate-800 hover:to-slate-700 disabled:opacity-50"
                     >
                        {menuPrefsSaving ? 'Opslaan…' : 'Opslaan'}
                     </button>
                  </div>
               </div>
            </div>
         )}

         {isSupportModalOpen && (
            <SupportSchedulerModal
               onClose={() => setIsSupportModalOpen(false)}
               prefillName={user.name}
               prefillEmail={(user as any).email}
            />
         )}

         {/* Floating Recruiter Agent - Removed per user request */}
         <PWAInstallPrompt />
      </div>
   );
};

export default Layout;
